import React, {
    useState,
    useRef,
    useEffect,
    useInsertionEffect,
    useLayoutEffect,
    useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import type { ButtonVariant } from './Button';
import MoreHorizontalIcon from './CustomIcons/MoreHorizontalIcon';
import { pushEscapeLayer } from './escapeLayers';
import {
    MENU_SPECS,
    type MenuGeometry,
    measureContentWidth,
    measureMenuGeometry,
} from './floatingMenuGeometry';
import { ensureOverlayStyles, overlayIdentity } from './portalOverlay';
import { useTopLayer } from './topLayer';

const LABEL_STYLE: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const EMPTY_ITEM_STYLE: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: 'var(--malachite-font-size-small, 13px)',
    color: 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
    userSelect: 'none',
};

export type ActionMenuItem =
    | {
          type?: 'item';
          key: string;
          label: React.ReactNode;
          onClick?: () => void;
          disabled?: boolean;
          danger?: boolean;
          tooltip?: string;
          icon?: React.ReactNode;
      }
    | {
          type: 'divider';
          key: string;
      };

export interface ActionMenuProps {
    items: ActionMenuItem[];
    label?: string;
    triggerIcon?: React.ReactNode;
    minWidth?: number;
    size?: 'small' | 'xsmall';
    /**
     * Shows `label` beside the icon instead of leaving it to the accessible name. For a menu
     * that is the point of its corner rather than an overflow for a row that already reads —
     * a kebab nobody can name is only safe when the row it belongs to says what it acts on.
     */
    showLabel?: boolean;
    /** Trigger's Button variant. Defaults to `outline`, the overflow-kebab look. */
    variant?: ButtonVariant;
}

// No box here: pinning `--malachite-control-height-small` beat Button's own `size-[28px]`, so a
// theme that moved that token moved the kebab and left the Button behind. Only the glyph is
// left to size, Button sizing one only for its `icon` prop.
const TRIGGER_SIZES: Record<'small' | 'xsmall', { icon: number; iconBox: string }> = {
    small: { icon: 16, iconBox: 'text-[16px] [&_svg]:size-[16px]!' },
    xsmall: { icon: 11, iconBox: 'text-[11px] [&_svg]:size-[11px]!' },
};

const itemStyle = (item: ActionMenuItem): React.CSSProperties => {
    if (item.type === 'divider') {
        return {
            height: 1,
            background: 'var(--malachite-card-border, rgb(232, 232, 232))',
            margin: '4px 0',
        };
    }
    return {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderRadius: 8,
        fontSize: 'var(--malachite-font-size-small, 13px)',
        fontWeight: 400,
        color: item.danger
            ? 'var(--malachite-text-destructive, rgb(237, 53, 54))'
            : 'var(--malachite-text-primary, rgb(10, 10, 10))',
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        opacity: item.disabled ? 0.5 : 1,
        userSelect: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        background: 'transparent',
        border: 'none',
        textAlign: 'left',
        width: '100%',
    };
};

/**
 * ActionMenu is a kebab-style overflow action menu. The trigger is a square
 * icon-only outline Button (three-dot icon by default); on click, a popover
 * appears with the given items. The popover is rendered to document.body via
 * createPortal so it escapes any Shadow DOM or overflow:hidden ancestors —
 * which is also why the popover is styled inline rather than with Tailwind:
 * the scoped stylesheet does not reach document.body.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({
    items,
    label = 'More actions',
    triggerIcon,
    minWidth,
    size = 'small',
    showLabel = false,
    variant = 'outline',
}) => {
    const trigger = TRIGGER_SIZES[size];
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<MenuGeometry | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const contentWidthRef = useRef<number | undefined>(undefined);

    useTopLayer(popoverRef, open);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) {
            return;
        }
        contentWidthRef.current =
            measureContentWidth(popoverRef.current) ?? contentWidthRef.current;

        setPosition(
            measureMenuGeometry(popoverRef.current, {
                triggerRect: trigger.getBoundingClientRect(),
                preset: 'options',
                minWidth,
                contentWidth: contentWidthRef.current,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
            }),
        );
    }, [minWidth]);

    useEffect(() => {
        if (!open) {
            setPosition(null);
            contentWidthRef.current = undefined;
            return;
        }
        updatePosition();
        const handleScrollOrResize = () => updatePosition();
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
                setOpen(false);
            }
        };
        // ESC via the shared layer stack: closes only the topmost overlay.
        const popEscapeLayer = pushEscapeLayer(() => setOpen(false));
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
            document.removeEventListener('mousedown', handleClickOutside, true);
            popEscapeLayer();
        };
    }, [open, updatePosition]);

    useLayoutEffect(() => {
        if (open && popoverRef.current) {
            updatePosition();
        }
    }, [open, updatePosition]);

    const handleItemClick = (item: ActionMenuItem) => {
        if (item.type === 'divider' || item.disabled) {
            return;
        }
        item.onClick?.();
        setOpen(false);
    };

    useInsertionEffect(() => {
        if (open) {
            ensureOverlayStyles();
        }
    }, [open]);

    const popoverStyle: React.CSSProperties = {
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        width: position?.width,
        maxHeight: position?.maxHeight,
        overflowY: 'auto',
        background: MENU_SPECS.options.background,
        border: MENU_SPECS.options.border,
        borderRadius: MENU_SPECS.options.radius,
        boxShadow: MENU_SPECS.options.shadow,
        padding: MENU_SPECS.options.padding,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        visibility: position ? 'visible' : 'hidden',
        opacity: position ? 1 : 0,
    };

    return (
        <>
            <Button
                ref={triggerRef}
                type="button"
                variant={variant}
                size={size}
                iconOnly={!showLabel}
                // With the label drawn, it is already the accessible name; repeating it here
                // would have a screen reader announce it twice.
                aria-label={showLabel ? undefined : label}
                aria-haspopup="menu"
                aria-expanded={open}
                // The engaged border while open is each variant's own, keyed off aria-expanded
                // in the stylesheet — pinning the outline token here stripped it from every
                // other variant.
                //
                // Button only spaces the icon it owns via its `icon` prop; this one is a child,
                // so the gap is set here rather than left to collapse against the label. `gap-2`
                // because the stylesheet is precompiled: a utility no source file already uses
                // is never emitted, and the class then does nothing with nothing to show for it.
                className={`shrink-0${showLabel ? ' gap-2' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* `!important` because every CustomIcon writes its size inline; `text-[…]` for a
                    glyph that is not an svg at all, which the svg rule never matches. */}
                <span className={`inline-flex items-center justify-center ${trigger.iconBox}`}>
                    {triggerIcon ?? <MoreHorizontalIcon size={trigger.icon} />}
                </span>
                {showLabel && <span>{label}</span>}
            </Button>
            {open &&
                createPortal(
                    <div
                        ref={popoverRef}
                        role="menu"
                        {...overlayIdentity('actionmenu')}
                        style={popoverStyle}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {items.length === 0 && (
                            // role="menu" takes menuitem children only, so the notice is one.
                            <div role="menuitem" aria-disabled="true" style={EMPTY_ITEM_STYLE}>
                                No actions available
                            </div>
                        )}
                        {items.map((item) =>
                            item.type === 'divider' ? (
                                <div key={item.key} style={itemStyle(item)} />
                            ) : (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="menuitem"
                                    disabled={item.disabled}
                                    title={item.tooltip}
                                    onClick={() => handleItemClick(item)}
                                    onMouseEnter={(e) => {
                                        if (!item.disabled) {
                                            (
                                                e.currentTarget as HTMLButtonElement
                                            ).style.background = 'rgba(0, 0, 0, 0.04)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.background =
                                            'transparent';
                                    }}
                                    style={itemStyle(item)}
                                >
                                    {/* Leading: every caller's icon names the action itself —
                                        copy, open, delete — and an icon that is part of an
                                        item's identity belongs where the eye starts, not
                                        right-aligned where it reads as a status column. */}
                                    {item.icon && (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                flexShrink: 0,
                                                color: 'var(--malachite-icon-secondary, rgb(148, 148, 148))',
                                            }}
                                        >
                                            {item.icon}
                                        </span>
                                    )}
                                    <span style={LABEL_STYLE}>{item.label}</span>
                                </button>
                            ),
                        )}
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default ActionMenu;
